import { breakpoints, colors, spacing } from 'src/design-tokens/primitives';

export const CHART_MAX_SERIES = 3;

// Recharts y-axis tick gutter (estimate; actual width depends on labels).
const Y_AXIS_GUTTER_ESTIMATE_PX = 24;

export const CHART_PALETTE = {
  1: [colors.blue[600]],
  2: [colors.blue[600], colors.blue[700]],
  3: [colors.blue[500], colors.blue[600], colors.blue[700]],
} as const satisfies Record<1 | 2 | 3, readonly string[]>;

/** Total bars (categories × series) at/above which a chart is treated as dense. */
export const DENSITY_BAR_THRESHOLD = 16;

/** Viewport width (px) at/above which bar charts use density-based orientation */
export const CHART_TABLET_MIN_WIDTH_PX = breakpoints.tablet;

/** Minimum px per vertical bar before value labels are shown chart-wide. */
export const CHART_VERTICAL_VALUE_LABEL_MIN_BAR_WIDTH_PX = 24;

export const CHART_HORIZONTAL_DIMENSIONS = {
  // Chart
  chartVerticalPaddingPx: 24,
  valueLabelRightMarginPx: 20,
  labelBarGapPx: 16,

  //Bar
  barThicknessPx: 18,
  barGapPx: 2,

  //Category
  categoryPaddingPx: 8,
  categoryLabelMaxChars: 32,
} as const;

export const CHART_DIMENSIONS = {
  // Chart
  defaultHeight: 200,
  marginTop: 16,
  marginRight: 8,
  marginLeft: 0,
  marginBottom: 0,
  plotContainerPaddingPx: spacing.chartFramePx * 2 + Y_AXIS_GUTTER_ESTIMATE_PX,

  // Bar
  barGap: 2,
  barCornerRadius: 2,
  maxBarSizePx: 120,

  // XAxis
  xAxisLabelGap: 8,
  xAxisLabelHeight: 24,

  // YAxis
  yAxisLabelPaddingTop: 16,
  yAxisLabelPaddingBottom: 0,
} as const;

export const CHART_AXIS = {
  tickFontFamily: 'Barlow, IBM Plex Sans, system-ui, sans-serif',
  tickFontSize: 12,
  tickColor: colors.gray[600],
  zeroLineColor: colors.navy[200],
} as const;

/** Supported x-axis category-label treatments for the vertical bar chart. */
export const CHART_CATEGORY_LABEL_MODES = [
  'auto',
  'truncate',
  'angle-30',
  'angle-45',
  'wrap',
] as const;

export type ChartCategoryLabelMode =
  (typeof CHART_CATEGORY_LABEL_MODES)[number];
export type EffectiveCategoryLabelMode = Exclude<
  ChartCategoryLabelMode,
  'auto'
>;

export interface ChartCategoryLabelLayout {
  /** Vertical band (px) reserved on the x-axis for category labels. */
  xAxisLabelHeight: number;
  /** Extra chart bottom margin (px) beyond the reserved band. */
  marginBottom: number;
  /** Left plot margin (px). */
  marginLeft: number;
  /** Right plot margin (px). */
  marginRight: number;
  /** Rotation (deg) for angled modes; null when not rotated. */
  angle: number | null;
  /** SVG text-anchor used to position the label under the bar. */
  textAnchor: 'start' | 'middle' | 'end';
  /** Max rendered lines before label clamp (wrap mode). */
  maxLines: number;
}

export const CHART_CATEGORY_LABEL_LAYOUT = {
  truncate: {
    xAxisLabelHeight: CHART_DIMENSIONS.xAxisLabelHeight,
    marginBottom: CHART_DIMENSIONS.marginBottom,
    marginLeft: CHART_DIMENSIONS.marginLeft,
    marginRight: CHART_DIMENSIONS.marginRight,
    angle: null,
    textAnchor: 'middle',
    maxLines: 1,
  },
  'angle-30': {
    xAxisLabelHeight: 36,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: CHART_DIMENSIONS.marginRight,
    angle: -30,
    textAnchor: 'end',
    maxLines: 1,
  },
  'angle-45': {
    xAxisLabelHeight: 44,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: CHART_DIMENSIONS.marginRight,
    angle: -45,
    textAnchor: 'end',
    maxLines: 1,
  },
  wrap: {
    xAxisLabelHeight: 40,
    marginBottom: 0,
    marginLeft: CHART_DIMENSIONS.marginLeft,
    marginRight: CHART_DIMENSIONS.marginRight,
    angle: null,
    textAnchor: 'middle',
    maxLines: 2,
  },
} as const satisfies Record<
  EffectiveCategoryLabelMode,
  ChartCategoryLabelLayout
>;

export const isAngledEffectiveMode = (
  mode: EffectiveCategoryLabelMode
): boolean => CHART_CATEGORY_LABEL_LAYOUT[mode].angle !== null;

export const CHART_VALUE_LABEL_TEXT = {
  fontFamily: CHART_AXIS.tickFontFamily,
  fontSize: CHART_AXIS.tickFontSize,
  fontWeight: 600,
  fill: colors.gray[800],
} as const;

export const NULL_VALUE_LABEL = 'N/A';

export const CHART_BAR_FALLBACK = {
  nullFill: colors.gray[200],
  nullLabelFill: colors.gray[500],
  zeroFillOpacity: 0.4,
  thicknessPx: 0.5,
} as const;
